// 總覽的 Kinio KMS 區塊（SPEC #kinio-kms-ui「總覽版型」、#vms-web-827 HEALTH-01）：VMS 區塊不動、IT 區塊只插入。
//   - KmgrOverviewColumns：有 kmgr.read（dashboard 有家戶）時把原版面放進左 8 欄、右 4 欄放 IT 區塊；沒有時原樣輸出（DOM 跟以前一樣）
//   - KmgrIncidentsBlock：插在「需要處理」之後——「現在要處理的事故」；組織 owner / org_admin 還沒啟用時改放啟用卡
//   - 右欄「家戶狀態」「網路與主機」（M2：探針 / 伺服器 / 收集器 / Kinio 設備的例外 + 設定待辦；收集器離線時 ⊘ 讀不到，KMS-25）；
//     安裝商代管（委派 API）、最近自動處置（M3-04 執行紀錄）kmgr 總覽還沒有資料，有再畫（UX-11）
// 每區各自標資料來源狀態（UX-04）；殼層先畫、資料到之前畫骨架、不顯示 0；更新中斷 > 30 秒整條注意色條。
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import { SkeletonLines } from '../../components/Skeleton';
import { timeStamp } from '../../lib/timefmt';
import { kmgrApi } from './api';
import { kmgrAdminOrgs, useKmgrDashboard, useKmgrEnv, useOrgKmgrStatus } from './hooks';
import { byOrgRows, kmgrScopeOf, sortHomes, sortIncidents, HOME_STATES, type IncidentFilter } from './model';
import { brandName, HomeStatePill, homeReason, homeStateIcon, IncidentRow, LiveStamp, NS } from './parts';
import { byOrgNetRows, networkRows, setupTodo } from './model';
import { NetUnreadableBar } from './widgets';
import type { KmgrDashboard } from './types';

const STALE_MS = 30_000;

function useStale(updatedAt: number, lastErrorAt: number): { stale: boolean; minutes: number } {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);
  const stale = !!updatedAt && lastErrorAt > updatedAt && now - updatedAt > STALE_MS;
  return { stale, minutes: Math.max(1, Math.round((now - updatedAt) / 60_000)) };
}

function hasKmgr(d: KmgrDashboard | null): boolean {
  return !!d && d.homes.total > 0;
}

/** 總覽左右兩欄的框；沒有 IT 區塊時原樣輸出。 */
export function KmgrOverviewColumns({ children }: { children: ReactNode }) {
  const { t } = useTranslation(NS);
  const dash = useKmgrDashboard();
  const { stale, minutes } = useStale(dash.updatedAt, dash.lastErrorAt);
  if (!hasKmgr(dash.data)) return <>{children}</>;
  return (
    <>
      {stale && (
        <div className="km-bar stale" role="status" data-testid="km-stale">
          <span className="ic" aria-hidden>
            ⚠
          </span>
          <span className="txt">{t('live.stale_bar', { n: minutes, time: timeStamp(dash.updatedAt, undefined, false) })}</span>
          <button className="btn btn-sm" onClick={dash.refetch}>
            {t('live.reconnect')}
          </button>
        </div>
      )}
      <div className="km-overview-grid km" data-testid="km-overview">
        <div className="km-main">{children}</div>
        <aside className="km-aside" aria-label={t('overview.it_blocks')}>
          <div style={{ textAlign: 'right', marginTop: -28 }}>
            <LiveStamp updatedAt={dash.updatedAt} stale={stale} />
          </div>
          <KmgrHomesBlock d={dash.data!} loading={dash.loading} />
          <KmgrNetworkBlock d={dash.data!} loading={dash.loading} />
        </aside>
      </div>
    </>
  );
}

/** 「需要處理」之後的插入點：事故區塊，或（還沒啟用的組織管理員）啟用卡。 */
export function KmgrIncidentsBlock() {
  const dash = useKmgrDashboard();
  if (hasKmgr(dash.data)) return <IncidentsCard d={dash.data!} stale={dash.lastErrorAt > dash.updatedAt && Date.now() - dash.updatedAt > STALE_MS} />;
  if (dash.loading) return null;
  return <EnableCard />;
}

function IncidentsCard({ d, stale }: { d: KmgrDashboard; stale: boolean }) {
  const { t } = useTranslation(NS);
  const { me } = useKmgrEnv();
  const { planes, scope, orgs } = useScope();
  const [f, setF] = useState<IncidentFilter>('all');
  const [conflict, setConflict] = useState<string | null>(null);
  const ks = kmgrScopeOf(scope);
  const api = planes[0]?.api ?? '';
  // 「沒人認領 / 我處理的」另查清單（總覽的前 5 筆是「全部」的）
  const filtered = useQuery({
    queryKey: ['kmgr', 'incidents', api, ks.scope, f, 'overview'],
    queryFn: () => kmgrApi.incidents(api, { scope: ks.scope, handling: 'open,acknowledged,claimed', claimed_by: f === 'unclaimed' ? 'none' : me, limit: 5 }),
    enabled: f !== 'all' && !!api && (f === 'unclaimed' || !!me),
  });
  const c = d.incidents.counts;
  const items = f === 'all' ? sortIncidents(d.incidents.items).slice(0, 5) : sortIncidents(filtered.data?.incidents ?? []).slice(0, 5);
  const crossOrg = new Set(d.incidents.items.map((i) => i.org_id)).size > 1 || orgs.filter((o) => o.kind === 'team').length > 1;
  const sevs = [c.p1 ? t('overview.sev_n_P1', { n: c.p1 }) : '', c.p2 ? t('overview.sev_n_P2', { n: c.p2 }) : '', c.p3 ? t('overview.sev_n_P3', { n: c.p3 }) : ''].filter(Boolean).join(' · ');
  const unavailable = d.incidents.source_state === 'unavailable';
  return (
    <section className="card km" data-testid="km-incidents" aria-labelledby="km-inc-h">
      <div className="km-block-head">
        <h2 id="km-inc-h">{t('overview.incidents_title')}</h2>
        {sevs && <span className="muted km-sub">{sevs}</span>}
        <span className="spacer" />
        <div className="km-seg" role="group" aria-label={t('overview.filter')}>
          {(['all', 'unclaimed', 'mine'] as const).map((k) => (
            <button key={k} type="button" aria-pressed={f === k} onClick={() => setF(k)}>
              {t(`filter.${k}`)}
            </button>
          ))}
        </div>
        {c.total > 0 && (
          <Link className="btn btn-sm" to="/kmgr/incidents">
            {t('overview.see_all', { n: c.total })}
          </Link>
        )}
      </div>
      {conflict && (
        <div className="km-bar warn" role="status">
          <span className="ic" aria-hidden>
            !
          </span>
          <span className="txt">{conflict}</span>
        </div>
      )}
      {unavailable ? (
        <div className="km-bar unk" role="status">
          <span className="ic" aria-hidden>
            ⊘
          </span>
          <span className="txt">{t('overview.incidents_unavailable')}</span>
        </div>
      ) : f !== 'all' && filtered.isLoading ? (
        <SkeletonLines n={2} label={t('common.loading')} />
      ) : items.length ? (
        <ul className="km-irows">
          {items.map((i) => (
            <IncidentRow key={i.incident_id} inc={i} me={me} crossOrg={crossOrg} onConflict={setConflict} />
          ))}
        </ul>
      ) : (
        <p className={'muted' + (stale ? ' km-stale-text' : '')} data-testid="km-incidents-none">
          {f === 'all' ? (d.hosts.total ? t('overview.none_watching_hosts', { homes: d.homes.total, hosts: d.hosts.total }) : t('overview.none_watching', { homes: d.homes.total })) : t('overview.none_filtered')}
        </p>
      )}
    </section>
  );
}

/** 右欄：家戶狀態（> 12 個只列非正常的，其餘收成「其餘 N 個正常」；手機只放計數晶片 + 前 3 個）。 */
function KmgrHomesBlock({ d, loading }: { d: KmgrDashboard; loading: boolean }) {
  const { t } = useTranslation(NS);
  const narrow = useNarrow();
  const homes = useMemo(() => sortHomes(d.homes.items), [d.homes.items]);
  const shown = narrow ? homes.filter((h) => h.state !== 'ok').slice(0, 3) : homes;
  const crossOrg = new Set(homes.map((h) => h.org_id)).size > 1;
  // 範圍含 2 個以上組織：依組織分組（by_org；前 10 個有事的 +「其餘 N 個組織正常」，M5-04）
  if (d.by_org && d.by_org.length >= 2) return <KmgrOrgsBlock d={d} loading={loading} />;
  return (
    <section className="card km" data-testid="km-homes" aria-labelledby="km-homes-h">
      <div className="km-block-head">
        <h2 id="km-homes-h">{t('overview.homes_title')}</h2>
        <span className="muted km-sub">{t('overview.homes_n', { n: d.homes.total })}</span>
      </div>
      {loading && !homes.length ? (
        <SkeletonLines n={2} label={t('common.loading')} />
      ) : d.homes.source_state === 'unavailable' ? (
        <div className="km-bar unk" role="status">
          <span className="ic" aria-hidden>
            ⊘
          </span>
          <span className="txt">{t('overview.homes_unavailable')}</span>
        </div>
      ) : (
        <>
          <div className="km-chips" aria-label={t('overview.homes_counts')}>
            {HOME_STATES.filter((s) => (d.homes.counts[s] ?? 0) > 0).map((s) => (
              <span key={s} className="km-chip" data-chip={s}>
                <span className={'ic ' + s} aria-hidden>
                  {homeStateIcon(s)}
                </span>{' '}
                {t(`home.${s}`)} {d.homes.counts[s]}
              </span>
            ))}
          </div>
          <ul className="km-hrows">
            {shown.map((h) => (
              <li key={h.home_id} className="km-hrow" data-home={h.home_id}>
                <div className="l1">
                  <strong>{crossOrg && h.org_name ? `${h.org_name} › ${h.home_name}` : h.home_name}</strong>
                  <span>
                    <HomeStatePill state={h.state} until={h.maintenance_until} />
                    {h.off_hours && <span className="muted km-sub"> ☾ {t('home.off_hours')}</span>}
                  </span>
                </div>
                {(homeReason(t, h) || h.baselining) && <div className="l2">{[homeReason(t, h), h.baselining ? t('home.baselining') : ''].filter(Boolean).join(' · ')}</div>}
              </li>
            ))}
          </ul>
          {d.homes.others_ok > 0 && <p className="muted km-sub">{t('overview.others_ok', { n: d.homes.others_ok })}</p>}
          {narrow && homes.length > shown.length && <p className="muted km-sub">{t('overview.homes_more', { n: homes.length - shown.length })}</p>}
        </>
      )}
    </section>
  );
}

/** 右欄：家戶狀態依組織分組（安裝商「全部」範圍；點組織 → 事故清單篩那個組織）。 */
function KmgrOrgsBlock({ d, loading }: { d: KmgrDashboard; loading: boolean }) {
  const { t } = useTranslation(NS);
  const { shown, othersOk, othersMore } = byOrgRows(d.by_org ?? []);
  return (
    <section className="card km" data-testid="km-homes" aria-labelledby="km-homes-h">
      <div className="km-block-head">
        <h2 id="km-homes-h">{t('overview.homes_title')}</h2>
        <span className="muted km-sub">{t('overview.orgs_homes_n', { orgs: d.by_org?.length ?? 0, n: d.homes.total })}</span>
      </div>
      {loading && !shown.length ? (
        <SkeletonLines n={2} label={t('common.loading')} />
      ) : (
        <>
          <div className="km-chips" aria-label={t('overview.homes_counts')}>
            {HOME_STATES.filter((s) => (d.homes.counts[s] ?? 0) > 0).map((s) => (
              <span key={s} className="km-chip" data-chip={s}>
                <span className={'ic ' + s} aria-hidden>
                  {homeStateIcon(s)}
                </span>{' '}
                {t(`home.${s}`)} {d.homes.counts[s]}
              </span>
            ))}
          </div>
          <ul className="km-orows" data-testid="km-by-org">
            {shown.map((o) => (
              <li key={o.org_id} className="km-orow" data-org={o.org_id}>
                <Link to={'/kmgr/incidents?org=' + encodeURIComponent(o.org_id)}>
                  <strong>{o.org_name ?? t('filter.org_unnamed')}</strong>
                </Link>
                <span className="muted km-sub">
                  {[
                    o.p1_unclaimed ? t('overview.org_p1_unclaimed', { n: o.p1_unclaimed }) : '',
                    o.homes_by_state.bad ? t('overview.org_bad_homes', { n: o.homes_by_state.bad }) : '',
                    o.homes_by_state.unreadable ? t('overview.org_unreadable_homes', { n: o.homes_by_state.unreadable }) : '',
                    !o.p1_unclaimed && o.incidents_open ? t('overview.org_open', { n: o.incidents_open }) : '',
                    o.hosts_offline ? t('overview.org_hosts_offline', { n: o.hosts_offline }) : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
          {othersOk > 0 && <p className="muted km-sub">{t('overview.orgs_others_ok', { n: othersOk })}</p>}
          {othersMore > 0 && <p className="muted km-sub">{t('overview.orgs_more', { n: othersMore })}</p>}
        </>
      )}
    </section>
  );
}

/**
 * 右欄：網路與主機——每列「狀態圖示 + 名稱 + 數字 + ›」，0 的列收成「其餘 N 項正常」；收集器離線時最上面 ⊘「讀不到網路設備（收集器〈名稱〉離線 N 分鐘）」，
 * 探針的數字不算（KMS-25：畫面沒有任何綠色的網路數字）；下方小節「設定待辦」（未授權的電腦；不是故障、不算進家戶狀態）。
 */
function KmgrNetworkBlock({ d, loading }: { d: KmgrDashboard; loading: boolean }) {
  const { t } = useTranslation(NS);
  const { rows, okRows } = networkRows(d);
  const todo = setupTodo(d);
  const net = d.network;
  const unreadable = net?.source_state === 'unreadable';
  const nothing = !net && !d.kinio && d.hosts.total === 0;
  if (loading && nothing) return null;
  return (
    <section className="card km" data-testid="km-network" aria-labelledby="km-net-h">
      <div className="km-block-head">
        <h2 id="km-net-h">{t('net.title')}</h2>
        {d.hosts.total > 0 && <span className="muted km-sub">{t('net.hosts_n', { n: d.hosts.total })}</span>}
      </div>
      {d.hosts.source_state === 'unavailable' ? (
        <div className="km-bar unk" role="status">
          <span className="ic" aria-hidden>
            ⊘
          </span>
          <span className="txt">{t('net.unavailable')}</span>
        </div>
      ) : (
        <>
          {unreadable && <NetUnreadableBar offline={net.collectors_offline} compact />}
          {rows.length > 0 && (
            <ul className="km-nrows">
              {rows.map((r) => (
                <li key={r.key}>
                  <Link className="km-nrow" to={r.to} data-row={r.key}>
                    <span className={'ic ' + r.level} aria-hidden>
                      {r.level === 'bad' ? '✕' : r.level === 'unk' ? '⊘' : '!'}
                    </span>
                    <span>{t(`net.row_${r.key}`)}</span>
                    <span className="n">{t(`net.n_${r.key}`, { n: r.n })}</span>
                    <span className="chev" aria-hidden>
                      ›
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {(d.by_org?.length ?? 0) > 1 && <NetByOrg d={d} />}
          {okRows > 0 && <p className="muted km-sub" data-testid="km-net-ok">{rows.length ? t('net.others_ok', { n: okRows }) : t('net.all_ok', { n: okRows })}</p>}
          {!unreadable && d.hosts.total > 0 && (
            <p className="muted km-sub" data-testid="km-net-ws">
              {t('net.hosts_line', { online: d.hosts.online, expected: d.hosts.expected_offline })}
            </p>
          )}
          {todo.length > 0 && (
            <>
              <h3 className="km-todo-title">
                {t('net.todo_title')} <span style={{ fontWeight: 400 }}>· {t('net.todo_note')}</span>
              </h3>
              <ul className="km-nrows">
                {todo.map((x) => (
                  <li key={x.key}>
                    <Link className="km-nrow" to={x.to} data-row={'todo_' + x.key}>
                      <span className="ic" aria-hidden>
                        ○
                      </span>
                      <span>{t(`net.todo_${x.key}`)}</span>
                      <span className="n">{t(x.key === 'ups_no_downstream' ? 'net.n_ups' : 'net.n_hosts', { n: x.n })}</span>
                      <span className="chev" aria-hidden>
                        ›
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}

/**
 * 「網路與主機」範圍跨組織時依組織分組（SPEC #kinio-kms-ui；Claude 推論待確認）：有事的組織前 10 個，每列「組織 › 讀不到 / 網路設備沒有回應 /
 * 伺服器離線 / 磁碟快滿 / 收集器離線」，點了進主機頁篩那個組織的異常電腦；其餘收成「其餘 N 個組織正常」。
 */
function NetByOrg({ d }: { d: KmgrDashboard }) {
  const { t } = useTranslation(NS);
  const { shown, othersOk, othersMore } = byOrgNetRows(d.by_org ?? []);
  if (!shown.length) return null;
  return (
    <>
      <h3 className="km-todo-title">{t('net.by_org_title')}</h3>
      <ul className="km-orows" data-testid="km-net-by-org">
        {shown.map(({ org, issues }) => (
          <li key={org.org_id} className="km-orow" data-org={org.org_id}>
            <Link to={`/kmgr/hosts?org=${encodeURIComponent(org.org_id)}&issue=1`}>
              <strong>{org.org_name ?? t('filter.org_unnamed')}</strong>
            </Link>
            <span className="muted km-sub">{issues.map((x) => t(`net.org_${x.key}`, { n: x.n })).join(' · ')}</span>
          </li>
        ))}
      </ul>
      {othersMore > 0 ? <p className="muted km-sub">{t('net.orgs_more', { n: othersMore })}</p> : othersOk > 0 ? <p className="muted km-sub">{t('net.orgs_others_ok', { n: othersOk })}</p> : null}
    </>
  );
}

function useNarrow(): boolean {
  const q = '(max-width: 600px)';
  const [m, setM] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(q).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(q);
    if (!mq) return;
    const on = () => setM(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return m;
}

const DISMISS_KEY = 'vms.kmgr.enable_card_dismissed';

/** 沒啟用時給組織 owner / org_admin 的卡：「用〈console_name〉看管這個〈家戶〉的網路與電腦〔開始〕」。 */
function EnableCard() {
  const { t } = useTranslation(NS);
  const { config } = useApp();
  const { orgs, org: scopeOrg } = useScope();
  const admin = kmgrAdminOrgs(orgs).filter((o) => o.kind === 'team');
  const org = admin.find((o) => o.org_id === scopeOrg?.org_id) ?? admin[0];
  const status = useOrgKmgrStatus(org);
  const [dismissed, setDismissed] = useState(() => readDismissed());
  if (!org || !status.data || status.data.homes.some((h) => h.kmgr_enabled) || dismissed.includes(org.org_id)) return null;
  const dismiss = () => {
    const next = [...dismissed, org.org_id];
    setDismissed(next);
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    } catch {
      /* 無 storage */
    }
  };
  return (
    <section className="card km" data-testid="km-enable-card" aria-labelledby="km-enable-h" style={{ borderLeft: '4px solid var(--accent)' }}>
      <h2 id="km-enable-h" style={{ margin: '0 0 8px', fontSize: 16 }}>
        {t('enable_card.title', { console: config.display_name, org: org.display_name })}
      </h2>
      <p>{t('enable_card.body')}</p>
      <p className="muted km-sub">{t('enable_card.note', { agent: t('agent_name', { brand: brandName(config.display_name) }), org: org.display_name })}</p>
      <div className="toolbar wrap" style={{ marginBottom: 0 }}>
        <Link className="btn btn-primary" to={`/org/kmgr/enable?org=${encodeURIComponent(org.org_id)}`}>
          {t('enable_card.start')}
        </Link>
        <button type="button" className="link-btn" onClick={dismiss}>
          {t('enable_card.later')}
        </button>
      </div>
    </section>
  );
}

function readDismissed(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
